import React, { createContext, useContext, useCallback, useMemo } from 'react';
import {
  useAuth0,
  RedirectLoginOptions,
  AppState,
  GetTokenSilentlyOptions,
  LogoutOptions,
} from '@auth0/auth0-react';

interface AuthContextProps {
  isAuthenticated: boolean;
  isLoadingCached: boolean;
  user: any; // You can replace `any` with a more specific type if you have one
  loginWithRedirect: (
    options?: RedirectLoginOptions<AppState>
  ) => Promise<void>;
  getAccessToken: (
    options?: GetTokenSilentlyOptions
  ) => Promise<string | undefined>;
  logout: (options?: LogoutOptions) => Promise<void>;
  handleRedirectCallback: (url?: string) => Promise<any>;
}

export const AuthContext = createContext<AuthContextProps | undefined>(
  undefined
);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    getAccessTokenSilently,
    logout,
    handleRedirectCallback,
  } = useAuth0();
  // Derive the context value directly — do NOT mirror useAuth0 into local state
  // via setState-in-useEffect. The Auth0 SDK returns fresh `user`/callback
  // references on most renders, so an effect keyed on them re-runs every render,
  // and because it also calls setState it self-triggers into a "Maximum update
  // depth exceeded" loop (reproduced by loginFlow.integration.test.tsx). useMemo
  // recomputes the value without scheduling extra renders, so the same reference
  // churn is harmless.
  const getAccessToken = useCallback(
    async (options?: GetTokenSilentlyOptions) => {
      try {
        return await getAccessTokenSilently(options);
      } catch (error: any) {
        console.error('Error fetching access token:', error?.message);
        return undefined;
      }
    },
    [getAccessTokenSilently]
  );

  const value = useMemo<AuthContextProps>(
    () => ({
      isAuthenticated,
      isLoadingCached: isLoading,
      user,
      loginWithRedirect,
      getAccessToken,
      logout,
      handleRedirectCallback,
    }),
    [
      isAuthenticated,
      isLoading,
      user,
      loginWithRedirect,
      getAccessToken,
      logout,
      handleRedirectCallback,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
